import React, { useMemo } from "react";
import Link from "next/link";
import { css } from "@emotion/react";
import { stringify } from "qs";
import { useTranslation } from "react-i18next";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { CruiseRouteListMap } from "./CruiseRouteListMap";
import { CruiseRouteListDynamicMap } from "./CruiseRouteListDynamicMap";
import { CruiseRouteList } from "./CruiseRouteList";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { Cruise } from "main/javascripts/types/cruise";
import { CruiseContentTitle } from "./CruiseContentTitle";
import { GoogleTagManagerEventTypes } from "main/javascripts/constants/GoogleTagManagerEventTypes";

export interface IProps {
  uniqueId: string;
  cruiseRoutes: Cruise.CruiseRoute[];
  departureDate?: string;
  cabinCode?: string;
  enableDynamicMap?: boolean;
  isEventTracking?: boolean;
}

export const CruiseRouteListModalBlock: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const {
    uniqueId,
    cruiseRoutes,
    departureDate,
    cabinCode,
    enableDynamicMap,
    isEventTracking,
  } = props;
  const { t } = useTranslation(["component"]);

  const showPagePath = useMemo(() => {
    if (departureDate) {
      const params = {
        departure_date: departureDate,
        ...(cabinCode && { cabin_code: cabinCode }),
      };
      const paramsString = stringify(params);
      return `/cruises/${uniqueId}?${paramsString}`;
    }
    return `/cruises/${uniqueId}`;
  }, [uniqueId, departureDate, cabinCode]);

  return (
    <div css={routesBlockStyle}>
      <CruiseContentTitle>{t("cruise.itinerary")}</CruiseContentTitle>
      <div css={innerStyle}>
        <div css={mapBlockStyle}>
          {enableDynamicMap ? (
            <div css={dynamicMapStyle}>
              <CruiseRouteListDynamicMap
                cruiseRoutes={cruiseRoutes}
                heightStyle="100%"
              />
            </div>
          ) : (
            <Link href={showPagePath} passHref>
              <a
                target="_blank"
                {...(isEventTracking
                  ? {
                      "data-event-category":
                        GoogleTagManagerEventTypes.clickCruiseShow,
                    }
                  : {})}
              >
                <CruiseRouteListMap cruiseRoutes={cruiseRoutes} />
              </a>
            </Link>
          )}
        </div>
        <div>
          <CruiseRouteList cruiseRoutes={cruiseRoutes} />
        </div>
      </div>
    </div>
  );
};

const routesBlockStyle = css`
  margin: 0;
`;
const innerStyle = css`
  ${moreThanBreakpoint("tablet")} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 4%;
    align-items: flex-start;
  }
`;
const mapBlockStyle = css`
  margin: ${space.atom2x} 0 ${space.atom3x};
  ${moreThanBreakpoint("tablet")} {
    position: sticky;
    top: 0;
  }
`;
const dynamicMapStyle = css`
  height: 18rem;
  ${moreThanBreakpoint("tablet")} {
    height: 28rem;
    max-height: calc(90vh - 10.5rem);
  }
`;
